import { Check, RotateCcw } from 'lucide-react'
import type { ToolDefinition, ToolOptions } from '../types'
import { defaultToolOptions } from '../types'

interface ToolOptionsPanelProps {
  tool: ToolDefinition
  options: ToolOptions
  onChange: (next: Partial<ToolOptions>) => void
}

function RangeField({
  label,
  value,
  min,
  max,
  unit = '%',
  onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  unit?: string
  onChange: (value: number) => void
}) {
  return (
    <label className="option-field range-field">
      <span>
        {label}
        <b>
          {value}
          {unit}
        </b>
      </span>
      <input type="range" min={min} max={max} value={value} onChange={(event) => onChange(Number(event.target.value))} />
    </label>
  )
}

function NumberField({ label, value, min = 0, onChange }: { label: string; value: number; min?: number; onChange: (value: number) => void }) {
  return (
    <label className="option-field">
      <span>{label}</span>
      <div className="number-input">
        <input type="number" min={min} value={value} onChange={(event) => onChange(Number(event.target.value))} />
        <em>px</em>
      </div>
    </label>
  )
}

function CheckField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <label className="check-field">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span className="check-box">{checked && <Check size={14} strokeWidth={3} />}</span>
      {label}
    </label>
  )
}

export function ToolOptionsPanel({ tool, options, onChange }: ToolOptionsPanelProps) {
  const reset = () => onChange(defaultToolOptions)

  return (
    <aside className="options-panel">
      <div className="options-heading">
        <div>
          <span>SETTING / 02</span>
          <h3>어떻게 손볼까</h3>
        </div>
        <button className="icon-button subtle" onClick={reset} type="button" title="설정 초기화">
          <RotateCcw size={17} />
        </button>
      </div>

      {tool.id === 'compress' && (
        <>
          <RangeField label="압축 품질" value={options.quality} min={25} max={95} onChange={(quality) => onChange({ quality })} />
          <p className="option-note">78% 전후면 용량과 화질이 무난하다. PNG는 실제로 작아질 때만 결과를 쓴다.</p>
        </>
      )}

      {tool.id === 'resize' && (
        <>
          <div className="segmented">
            <button className={options.resizeMode === 'pixels' ? 'active' : ''} onClick={() => onChange({ resizeMode: 'pixels' })}>픽셀</button>
            <button className={options.resizeMode === 'percent' ? 'active' : ''} onClick={() => onChange({ resizeMode: 'percent' })}>퍼센트</button>
          </div>
          {options.resizeMode === 'pixels' ? (
            <div className="option-grid">
              <NumberField label="최대 너비" value={options.width} min={1} onChange={(width) => onChange({ width })} />
              <NumberField label="최대 높이" value={options.height} min={1} onChange={(height) => onChange({ height })} />
            </div>
          ) : (
            <RangeField label="크기 비율" value={options.percent} min={10} max={200} onChange={(percent) => onChange({ percent })} />
          )}
          <CheckField label="가로세로 비율 유지" checked={options.keepAspect} onChange={(keepAspect) => onChange({ keepAspect })} />
          <CheckField label="작은 이미지는 확대하지 않기" checked={options.noUpscale} onChange={(noUpscale) => onChange({ noUpscale })} />
        </>
      )}

      {tool.id === 'crop' && (
        <>
          <div className="option-grid">
            <NumberField label="X 위치" value={options.cropX} onChange={(cropX) => onChange({ cropX })} />
            <NumberField label="Y 위치" value={options.cropY} onChange={(cropY) => onChange({ cropY })} />
            <NumberField label="자를 너비" value={options.cropWidth} min={1} onChange={(cropWidth) => onChange({ cropWidth })} />
            <NumberField label="자를 높이" value={options.cropHeight} min={1} onChange={(cropHeight) => onChange({ cropHeight })} />
          </div>
          <p className="option-note">왼쪽 위가 0, 0이다. 이미지 밖으로 나간 값은 자동으로 맞춘다.</p>
        </>
      )}

      {tool.id === 'convert-jpg' && (
        <>
          <RangeField label="JPG 품질" value={options.quality} min={35} max={100} onChange={(quality) => onChange({ quality })} />
          <p className="option-note">투명한 영역은 흰색으로 채운다.</p>
        </>
      )}

      {tool.id === 'convert-png' && (
        <>
          <label className="option-field">
            <span>결과 포맷</span>
            <select value={options.outputFormat} onChange={(event) => onChange({ outputFormat: event.target.value as ToolOptions['outputFormat'] })}>
              <option value="image/png">PNG · 선명한 무손실</option>
              <option value="image/webp">WEBP · 작고 가벼움</option>
            </select>
          </label>
          {options.outputFormat === 'image/webp' && (
            <RangeField label="WEBP 품질" value={options.quality} min={35} max={100} onChange={(quality) => onChange({ quality })} />
          )}
        </>
      )}

      {tool.id === 'rotate' && (
        <>
          <span className="field-label">회전 각도</span>
          <div className="choice-grid three">
            {([-90, 90, 180] as const).map((rotation) => (
              <button key={rotation} className={options.rotation === rotation ? 'active' : ''} onClick={() => onChange({ rotation })}>
                {rotation === -90 ? '왼쪽 90°' : rotation === 90 ? '오른쪽 90°' : '180°'}
              </button>
            ))}
          </div>
          <CheckField label="좌우 뒤집기" checked={options.flipHorizontal} onChange={(flipHorizontal) => onChange({ flipHorizontal })} />
          <CheckField label="상하 뒤집기" checked={options.flipVertical} onChange={(flipVertical) => onChange({ flipVertical })} />
        </>
      )}

      {tool.id === 'watermark' && (
        <>
          <label className="option-field">
            <span>워터마크 문구</span>
            <input type="text" value={options.watermarkText} maxLength={40} onChange={(event) => onChange({ watermarkText: event.target.value })} />
          </label>
          <div className="option-grid">
            <NumberField label="글자 크기" value={options.fontSize} min={12} onChange={(fontSize) => onChange({ fontSize })} />
            <label className="option-field">
              <span>글자 색</span>
              <input className="color-input" type="color" value={options.textColor} onChange={(event) => onChange({ textColor: event.target.value })} />
            </label>
          </div>
          <RangeField label="불투명도" value={options.opacity} min={10} max={100} onChange={(opacity) => onChange({ opacity })} />
          <label className="option-field">
            <span>위치</span>
            <select value={options.watermarkPosition} onChange={(event) => onChange({ watermarkPosition: event.target.value as ToolOptions['watermarkPosition'] })}>
              <option value="top-left">왼쪽 위</option>
              <option value="top-right">오른쪽 위</option>
              <option value="center">가운데</option>
              <option value="bottom-left">왼쪽 아래</option>
              <option value="bottom-right">오른쪽 아래</option>
            </select>
          </label>
        </>
      )}

      {tool.id === 'editor' && (
        <>
          <RangeField label="밝기" value={options.brightness} min={25} max={175} onChange={(brightness) => onChange({ brightness })} />
          <RangeField label="대비" value={options.contrast} min={25} max={175} onChange={(contrast) => onChange({ contrast })} />
          <RangeField label="채도" value={options.saturation} min={0} max={200} onChange={(saturation) => onChange({ saturation })} />
          <RangeField label="흑백" value={options.grayscale} min={0} max={100} onChange={(grayscale) => onChange({ grayscale })} />
        </>
      )}

      {tool.id === 'upscale' && (
        <>
          <span className="field-label">확대 배율</span>
          <div className="choice-grid">
            {([2, 4] as const).map((upscale) => (
              <button key={upscale} className={options.upscale === upscale ? 'active' : ''} onClick={() => onChange({ upscale })}>
                <strong>{upscale}×</strong>
                <small>{upscale === 2 ? '일반 확대' : '초고해상도'}</small>
              </button>
            ))}
          </div>
          <p className="option-note">브라우저 보간으로 키운다. 없던 디테일을 만드는 AI 확대는 아니다.</p>
        </>
      )}

      {tool.id === 'remove-bg' && (
        <>
          <RangeField label="배경 감지 범위" value={options.tolerance} min={4} max={70} unit="" onChange={(tolerance) => onChange({ tolerance })} />
          <p className="option-note">네 모서리 색을 기준으로 단색 배경을 투명하게 만든다. 복잡한 배경은 낮은 값부터 확인해라.</p>
        </>
      )}

      {tool.id === 'meme' && (
        <>
          <label className="option-field">
            <span>위 문구</span>
            <input type="text" value={options.memeTop} maxLength={60} onChange={(event) => onChange({ memeTop: event.target.value })} />
          </label>
          <label className="option-field">
            <span>아래 문구</span>
            <input type="text" value={options.memeBottom} maxLength={60} onChange={(event) => onChange({ memeBottom: event.target.value })} />
          </label>
        </>
      )}

      {tool.id === 'blur' && (
        <>
          <RangeField label="흐림 강도" value={options.blur} min={2} max={40} unit="px" onChange={(blur) => onChange({ blur })} />
          <p className="option-note">현재는 이미지 전체를 흐린다. 공개하기 전에 정보가 충분히 가려졌는지 직접 확인해라.</p>
        </>
      )}
    </aside>
  )
}
